// react
import { useMemo } from 'react';
import { useOutlet } from 'react-router-dom';
import styled from 'styled-components';

// components
import { Banner, Icon, NavBar, NavBarTemplate, theme } from '@karehero/llama';

// store
import { useSelector } from 'store';
import { getAccount } from 'store/slices/iamSlice';
import { apiUrl } from 'store/api';

// hooks
import { useApp, useAuth, useWindowSize } from 'hooks';

// launch darkly
import { useFlags } from 'launchdarkly-react-client-sdk';

/**
 * Root is the main component of the application.
 */
const Root = () => {
  // hooks
  const currentOutlet = useOutlet();
  const windowSize = useWindowSize();
  const account = useSelector(getAccount);
  const { isApp, isMobileBrowser } = useApp();
  const { signOut } = useAuth();
  const flags = useFlags();

  // memo
  const isMobile = useMemo(() => {
    return windowSize[0] <= theme.breakpoint.smValue;
  }, [windowSize]);

  const homePath = useMemo(() => {
    if (!account || account.accountRole.name !== 'KareHero Administrator') {
      return `/`;
    }
    return '/admin';
  }, [account]);

  return (
    <RootWrapper>
      <NavBarTemplate
        navBar={
          account && (
            <NavBar
              role={account?.accountRole?.name || 'User'}
              name={`${account?.firstName || ''} ${account?.lastName || ''}`}
              fallback={`${account?.firstName?.[0] || ''}${
                account?.lastName?.[0] || ''
              }`}
              avatarSrc={account?.kindeUser?.picture}
              toHome={homePath}
              toAccountSettings='/settings'
              onSignOut={signOut}
              isMobile={isMobile}
            />
          )
        }
        banner={
          <>
            {!isApp && isMobileBrowser && flags.appStoreBanner && (
              <Banner
                to={`${apiUrl}/v1/app-store`}
                content={
                  <>
                    <div>
                      Download our app <u>here</u>
                    </div>
                    <Icon icon='arrow-down-to-bracket' fontSize={18} />
                  </>
                }
              />
            )}
          </>
        }
        content={currentOutlet}
      />
    </RootWrapper>
  );
};

const RootWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

Root.displayName = 'Root';
export default Root;
